import { Action, ActionCreator, Dispatch } from "redux";
import { ActionTypes } from "./ActionTypes";
import { PortalState } from "../../types/PortalState";
import deleteAsset from "../../api/DeleteAsset";
import { AssetInfo } from "../../types/AssetInfo";
import { AssetType } from "../../types/AssetType";
import { setSideBarData } from "./SideBar";
import { getSideBarForInspection } from "../../types/SideBarData";
import { AssetState } from "../../types/AssetState";
import Asset from "../../types/Asset";
import {
    requestAssetMetaData,
    uploadAsset
} from "../../services/UploadService";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import addMetadata from "../../api/AddMetadata";
import { ValidationType } from "../../types/CheckList";
import { defaultUIStatus, UIStatus, WarningType } from "../../types/UIStatus";
import { Inspection } from "../../types/Inspection";
import { ImageDeleteData } from "../../types/ImageDeleteData";
import getUserTypeText from "../../helpers/users";
import {CognitoUserInterface} from "@aws-amplify/ui-components";
import {updateDocumentExpiryDate} from "./Inspections";
import {formatDate} from "../../types/ReUseAsset";
import { DefectItem } from "../../types/DefectItem";

const FIVE_MINUTE_SOCKET_TIMEOUT = 300000;
let timeoutReference: any = null;

const addCommentToAssetInStore: ActionCreator<Action> = (asset: AssetInfo) => {
    const action = ActionTypes.ADD_COMMENT;
    return {
        type: action,
        asset: asset
    };
};

export const addCommentAction = (
    inspectionId: string,
    elementId: string,
    assetId: string,
    assetType: AssetType,
    commentField: string,
    commentValue: string
) => {
    return async (dispatch: Dispatch<Action>, getState: () => PortalState) => {
        const currentSession = await Auth.currentSession();
        const idToken = currentSession.getIdToken().getJwtToken();

        const inspection =
            getState().portal.inspectionDetails.get(inspectionId);

        try {
            const asset: AssetInfo = {
                assetId: assetId,
                assetType: assetType,
                elementId: elementId,
                id: inspection.Id,
                metadata: { comments: commentValue }
            };

            await addMetadata(assetId, commentField, commentValue, idToken);
            dispatch(addCommentToAssetInStore(asset));
        } catch (e) {
            toast.error(e);
        }
    };
};

const MAIN_IMAGE_ELEMENT_NAME = "Vessel Image";

export const deleteAssetInStore: ActionCreator<Action> = (asset: AssetInfo) => {
    let action;

    switch (asset.assetType) {
        case AssetType.CheckList:
            action = ActionTypes.DELETE_CHECKLIST;
            break;
        case AssetType.Document:
            action = ActionTypes.DELETE_DOCUMENT;
            break;
        case AssetType.Image:
            action = ActionTypes.DELETE_IMAGE;
            break;
    }

    return {
        type: action,
        asset: asset
    };
};

const updateUiAfterDelete = (
    assetForDelete: any,
    assetType: AssetType,
    inspection: Inspection,
    dispatch: Dispatch<Action>
) => {
    const asset: AssetInfo = {
        assetId: assetForDelete.assetId,
        assetType: assetType,
        elementId: assetForDelete.elementId,
        id: inspection.Id,
        metadata: {}
    };

    const elementName = getElementNameFromInspection(
        inspection,
        assetForDelete.elementId
    );

    if (elementName === MAIN_IMAGE_ELEMENT_NAME) {
        asset.mainShip = true;
    }

    if (assetType === AssetType.Image) {
        inspection.NumberOfPhotos--;
    }
    if (assetType === AssetType.Document) {
        inspection.NumberOfDocuments--;
    }

    const sideBarData = getSideBarForInspection(inspection);

    if (assetType === AssetType.CheckList) {
        sideBarData.numberOfCheckLists--;
    }

    dispatch(setSideBarData(sideBarData));
    dispatch(deleteAssetInStore(asset));
};

export const clearDeleteData: ActionCreator<Action> = () => {
    return {
        type: ActionTypes.CLEAR_DELETE_DATA
    };
};

export const deleteAssetsBulkAction = (
    inspectionId: string,
    deleteData: ImageDeleteData[],
    assetType: AssetType
) => {
    return async (dispatch: Dispatch<Action>, getState: () => PortalState) => {
        const currentSession = await Auth.currentSession();
        const idToken = currentSession.getIdToken().getJwtToken();

        const inspection =
            getState().portal.inspectionDetails.get(inspectionId);

        const assetIds: string[] = deleteData.map((asset) => asset.assetId);

        await deleteAsset(inspection.Id, assetIds, idToken);

        deleteData.forEach((assetForDelete) => {
            updateUiAfterDelete(
                assetForDelete,
                assetType,
                inspection,
                dispatch
            );
        });

        dispatch(clearDeleteData());
    };
};

export const deleteAssetAction = (
    inspectionId: string,
    elementId: string,
    assetId: string,
    assetType: AssetType,
    reuseDocumentelement: boolean,
) => {
    return async (dispatch: Dispatch<Action>, getState: () => PortalState) => {
        const currentSession = await Auth.currentSession();
        const idToken = currentSession.getIdToken().getJwtToken();

        const inspection =
            getState().portal.inspectionDetails.get(inspectionId);

        reuseDocumentelement 
            ? await deleteAsset(inspection.Id, [assetId], idToken, elementId)
            : await deleteAsset(inspection.Id, [assetId], idToken)

        updateUiAfterDelete(
            { assetId: assetId, elementId: elementId },
            assetType,
            inspection,
            dispatch
        );
    };
};

export const addAssetInStore = (
    id: string,
    elementId: string,
    assets: Asset[]
) => {
    return {
        type: ActionTypes.UPLOAD_ASSET,
        assetInfo: {
            assets: assets,
            id: id,
            elementId: elementId
        }
    };
};

export const updateUIStatus = (uiStatus: UIStatus) => {
    return {
        type: ActionTypes.UPDATE_UI_STATUS,
        uiStatus: uiStatus
    };
};

export const updateCheckListViaSocketDetails = async (
    data: any,
    dispatch: any,
    activeInspection: Inspection
) => {
    const metadata = data.metadata;
    const inspectionId = metadata["inspection-id"];
    const assetId = metadata["asset-id"];
    const elementId = metadata["element-id"];
    const fileName = metadata["filename"];

    const assetFailedToVerify =
        data.success === "False" || data.success === false;
    clearTimeout(timeoutReference);

    if (assetFailedToVerify) {
        const critical = "critical" as WarningType;
        const warningBanner = {
            warningBannerTitle: data.message,
            warningBannerStatus: critical,
            warningBannerEnabled: true
        };

        const assetToDelete: any = {
            id: inspectionId,
            assetId: assetId,
            url: data.presigned_url,
            state: AssetState.NO_ASSET,
            assetType: AssetType.CheckList,
            elementId: parseInt(metadata["element-id"])
        };

        dispatch(updateUIStatus(warningBanner));
        dispatch(deleteAssetInStore(assetToDelete));
        dispatch(setSideBarData(getSideBarForInspection(activeInspection)));
    } else {
        const assetVerified: any = {
            id: assetId,
            url: data.signedGetUrl,
            name: fileName,
            state: AssetState.DISPLAYING,
            progress: 100,
            type: AssetType.CheckList,
            elementId: elementId,
            metadata: data
        };

        dispatch(updateUIStatus(defaultUIStatus));
        dispatch(
            updateAssetInStore(inspectionId, parseInt(elementId), assetVerified)
        );
    }
};

export const updateAssetInStore = (
    id: string,
    elementId: any,
    asset: Asset
) => {
    return {
        type: ActionTypes.UPDATE_ASSET,
        assetInfo: {
            asset: asset,
            id: id,
            elementId: elementId
        }
    };
};

const getElementNameFromInspection = (inspection: any, elementId: any) => {
    for (let i = 0; i !== inspection.sections.length; i++) {
        if (inspection.sections[i].Id === elementId) {
            return inspection.sections[i].Name;
        }
    }
};

const getSurveyElementId = (inspection: any, inspectionElementId: string) => {
    for (let i = 0; i !== inspection.sections.length; i++) {
        if (inspection.sections[i].Id === inspectionElementId) {
            return inspection.sections[i].SurveyElementId;
        }
    }
}

const getAssetName = (assetType: AssetType) => {
    switch (assetType) {
        case AssetType.CheckList:
            return "document";
        case AssetType.Image:
            return "image";
        case AssetType.Document:
            return "document";

        default:
            return "document";
    }
};

const setTimeoutFunction = (
    asset: any,
    elementId: string,
    isMainShipImage: boolean,
    inspection: Inspection,
    validation: ValidationType,
    dispatch: any
) => {
    if (validation === ValidationType.PDF_CHECKLIST) {
        asset.progress = 100;
        asset.state = AssetState.PROCESSING;
        timeoutReference = setTimeout(() => {
            const critical = "critical" as WarningType;
            const warningBanner = {
                warningBannerTitle:
                    "A problem has occurred. Please check your data connection and resubmit.",
                warningBannerStatus: critical,
                warningBannerEnabled: true
            };

            const assetToDelete: any = {
                id: inspection.Id,
                assetId: asset.id,
                state: AssetState.NO_ASSET,
                assetType: AssetType.CheckList,
                elementId: parseInt(elementId)
            };

            dispatch(deleteAssetInStore(assetToDelete));
            dispatch(setSideBarData(getSideBarForInspection(inspection)));
            dispatch(updateUIStatus(warningBanner));
        }, FIVE_MINUTE_SOCKET_TIMEOUT);
    } else {
        asset.progress = 100;
        asset.state = AssetState.DISPLAYING;

        if (asset.type === AssetType.Image) {
            inspection.NumberOfPhotos++;
        }

        if (asset.type === AssetType.Document) {
            inspection.NumberOfDocuments++;
        }

        if (isMainShipImage) {
            asset.mainShip = true;
        }
    }

    dispatch(updateAssetInStore(inspection.Id, elementId, asset));
    dispatch(setSideBarData(getSideBarForInspection(inspection)));
};

export const uploadAssets = (
    id: string,
    elementId: string,
    assets: Asset[],
    validation: ValidationType = ValidationType.NONE,
    user: CognitoUserInterface
) => {
    return async (dispatch: any, getState: any) => {
        const currentSession = await Auth.currentSession();
        const idToken = currentSession.getIdToken().getJwtToken();

        const state = getState();
        const inspection = state.portal.inspectionDetails.get(id);
        const elementName = getElementNameFromInspection(inspection, elementId);
        const surveyElementId = getSurveyElementId(inspection, elementId);

        const assetUploadedBy = getUserTypeText(state.portal.userType);

        let isMainShipImage = false;

        if (elementName === MAIN_IMAGE_ELEMENT_NAME) {
            isMainShipImage = true;
        }


        dispatch({
            type: ActionTypes.SAVING_EXPIRY_DATE_SUCCESS,
            section: elementId,
            newExpiryDate: assets[0].expiryTime
        });

        dispatch(addAssetInStore(id, elementId, assets));

        for (let index = 0; index < assets.length; index++) {
            const asset: any = assets[index];
            const assetName = getAssetName(asset.type);

            let asimsSlot = "final";
            if (asset.type === AssetType.CheckList) {
                asimsSlot = "surveyor";
            }

            let assetMetadata: any;

                try {
                const apiResult = await requestAssetMetaData(
                    idToken,
                    elementId,
                    elementName,
                    asimsSlot,
                    assetName,
                    isMainShipImage,
                    validation,
                    assetUploadedBy,
                    inspection.OrganizationId,
                    asset,
                    inspection,
                    surveyElementId,
                    user.username
                );

                assetMetadata = apiResult;

                asset.id = assetMetadata.assetId;

                let uploadUrl = "";
                let validationCallbackUrl = "";

                if (validation === ValidationType.PDF_CHECKLIST) {
                    uploadUrl = assetMetadata.uploadUrl;
                    validationCallbackUrl = assetMetadata.callbackUrl;
                } else {
                    uploadUrl = assetMetadata.putUrl;
                }

                await uploadAsset(
                    asset.file,
                    inspection.ShipIMO,
                    idToken,
                    elementId,
                    inspection.Id,
                    elementName,
                    inspection.CreatedAt,
                    inspection.InspectionNumber,
                    assetName,
                    asset.id,
                    uploadUrl,
                    asimsSlot,
                    (event: any) => {
                        asset.progress = Math.round(
                            (100 * event.loaded) / event.total
                        );
                        dispatch(updateAssetInStore(id, elementId, asset));
                    },
                    isMainShipImage,
                    validationCallbackUrl,
                    user.username
                );


                setTimeoutFunction(
                    asset,
                    elementId,
                    isMainShipImage,
                    inspection,
                    validation,
                    dispatch
                );
            } catch (err) {
                toast.error(err);
            }
        }

        const date = assets[0].expiryTime ? formatDate(parseInt(assets[0].expiryTime.toString())) : null;
        if(date && assets[0].reusable) {
            dispatch(
                updateDocumentExpiryDate(
                    date,
                    elementId,
                    assets
                )
            )
        }

    };
};